import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import rgfLauncher from '@/assets/rgf-launcher.png';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();


  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rgf-primary-50 to-white">
        <div className="text-center">
          <div className="mb-6">
            <img
              className="h-16 w-16 mx-auto animate-pulse"
              src={rgfLauncher}
              alt="Loading"
            />
          </div>
          <p className="text-rgf-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required roles are specified
  if (requiredRoles.length > 0) {
    // If user is not loaded yet but we're authenticated, show loading
    if (!user) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rgf-primary-50 to-white">
          <div className="text-center">
            <div className="mb-6">
              <img
                className="h-16 w-16 mx-auto animate-pulse"
                src={rgfLauncher}
                alt="Loading"
              />
            </div>
            <p className="text-rgf-gray-600 text-lg">Loading user permissions...</p>
          </div>
        </div>
      );
    }

    const userRoles = user.roles || [];
    const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));


    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-rgf-gray-50">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg 
                className="h-6 w-6 text-red-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.232 18.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-rgf-gray-900 mb-2">
              Access Denied
            </h3>
            <p className="text-rgf-gray-600 mb-4">
              You don't have permission to access this resource.
            </p>
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mb-4">
                <p>Required roles: {requiredRoles.join(', ')}</p>
                <p>Your roles: {userRoles.join(', ')}</p>
              </div>
            )}
            <button
              onClick={() => window.history.back()}
              className="btn-secondary"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};